/* eslint-disable @atlaskit/platform/one-value-export-per-file -- cohesive slot registry: insert/remove/subscribe share one store */

/**
 * Extra content above an assistant message's thumbs/copy footer.
 *
 * Editor and chat are sibling React trees, so this is a module-level registry
 * rather than a React context. The Create-with-Rovo staging editor runs in a
 * same-origin iframe; the store is hung on `window.top` so iframe inserts are
 * visible to parent chat. Payloads must stay serializable flags + `onAction`
 * — parent React cannot render an iframe-created component.
 *
 * Reading a property on `window.top` throws `SecurityError` when chat is framed
 * on a third-party page (Rovo Extension). Fall back to this frame's `window`
 * in that case — Review moment is not used there. See HOT-306537 / ASIMO-3773.
 */

export type MessageFooterSlotFilterContext = {
	/** Assistant message this slot is rendering on. */
	messageId?: string;
};

/**
 * Enabled state for the shared Review moment bar. Chat owns chrome and copy;
 * the inserting surface owns undo / redo / diff.
 */
export type ReviewMomentFooterState = {
	canRedo: boolean;
	canUndo: boolean;
	isDisplayingDiff: boolean;
};

export type MessageFooterSlotItem = {
	filter?: (ctx: MessageFooterSlotFilterContext) => boolean;
	id: string;
	/** Safe across a same-origin iframe. */
	onAction?: (actionId: string) => void;
	reviewMoment: ReviewMomentFooterState;
};

const STORE_KEY = '__rovoMessageFooterSlotStore';

type MessageFooterSlotStore = {
	/**
	 * Bumped by {@link invalidateFooterItems}. Inserts/removes that still
	 * carry an older generation are ignored so a leftover editor (previous
	 * document / iframe) cannot put View changes or Undo/Redo back into chat.
	 */
	generation: number;
	items: MessageFooterSlotItem[];
	listeners: Set<() => void>;
};

type StoreRoot = Window & {
	[STORE_KEY]?: MessageFooterSlotStore;
};

const EMPTY_ITEMS: MessageFooterSlotItem[] = [];

const ssrStore: MessageFooterSlotStore = {
	generation: 0,
	items: EMPTY_ITEMS,
	listeners: new Set(),
};

const getStoreRoot = (): StoreRoot | null => {
	if (typeof window === 'undefined') {
		return null;
	}
	try {
		const top = (window.top ?? window) as StoreRoot;
		// Reading `window.top` is allowed; reading a property on a
		// cross-origin `top` throws. Probe before returning.
		void top[STORE_KEY];
		return top;
	} catch {
		return window as StoreRoot;
	}
};

const getStore = (): MessageFooterSlotStore => {
	const root = getStoreRoot();
	if (!root) {
		return ssrStore;
	}
	if (!root[STORE_KEY]) {
		root[STORE_KEY] = {
			generation: 0,
			items: EMPTY_ITEMS,
			listeners: new Set(),
		};
	}
	return root[STORE_KEY];
};

const emit = (store: MessageFooterSlotStore): void => {
	store.listeners.forEach((listener) => listener());
};

export const insertFooterItem = (item: MessageFooterSlotItem, generation?: number): void => {
	const store = getStore();
	if (generation !== undefined && generation !== store.generation) {
		return;
	}
	store.items = [...store.items.filter((existing) => existing.id !== item.id), item];
	emit(store);
};

export const removeFooterItem = (id: string, generation?: number): void => {
	const store = getStore();
	if (generation !== undefined && generation !== store.generation) {
		return;
	}
	const next = store.items.filter((existing) => existing.id !== id);
	if (next.length === store.items.length) {
		return;
	}
	store.items = next.length === 0 ? EMPTY_ITEMS : next;
	emit(store);
};

export const clearFooterItems = (): void => {
	const store = getStore();
	if (store.items.length === 0) {
		return;
	}
	store.items = EMPTY_ITEMS;
	emit(store);
};

/**
 * Drop every item and bump the generation so stale editors cannot re-insert
 * or remove the current document's footer. Used when the conversation
 * changes or a create session starts (creates never show View changes).
 */
export const invalidateFooterItems = (): void => {
	const store = getStore();
	store.generation += 1;
	if (store.items.length === 0) {
		return;
	}
	store.items = EMPTY_ITEMS;
	emit(store);
};

export const getFooterSlotGeneration = (): number => getStore().generation;

export const subscribeFooterSlot = (onStoreChange: () => void): (() => void) => {
	const store = getStore();
	store.listeners.add(onStoreChange);
	return () => {
		store.listeners.delete(onStoreChange);
	};
};

export const getFooterSlotItems = (): MessageFooterSlotItem[] => getStore().items;

export const getFooterSlotItemsServerSnapshot = (): MessageFooterSlotItem[] => EMPTY_ITEMS;
